import type { CognitoUserSession } from 'amazon-cognito-identity-js';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoRefreshToken } from 'amazon-cognito-identity-js';

type ApiGateway = {
  user_pool_id?: string;
  app_client_id?: string;
  user_pool_url?: string;
  [key: string]: any;
};

type PasswordChallengeEnum = 'Success' | 'Failure';
type RefreshTokenEnum = 'Success' | 'Failure';

type LoginDto = {
  email: string;
  password: string;
  companyName: string;
};

const LoginService = {
  async doLogin(values: LoginDto, apiGateway: ApiGateway): Promise<{jwtToken?: string; result?: object; newPasswordRequired?: boolean; cognitoUser?: CognitoUser; failure?: boolean; errorMessage?: string;}> {
    const authenticationData = {
      Username: values.email.toLowerCase(),
      Password: values.password,
    };
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    const poolData = {
      UserPoolId: "us-east-1_af2GRumEQ",
      ClientId: "28qrq8nr4tih8odvf11ufs1a9s",
    };
    const userPool = new CognitoUserPool(poolData);
    const userData = {
      Username: values.email.toLowerCase(),
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          this.storeUserAfterSuccessfullyLogin(result, cognitoUser, apiGateway);
          const jwtToken = result.getAccessToken().getJwtToken();
          resolve({ jwtToken });
        },
        newPasswordRequired: (userAttributes) => {
          delete userAttributes.email_verified;
          delete userAttributes.phone_number_verified;

          resolve({
            result: userAttributes,
            newPasswordRequired: true,
            cognitoUser,
          });
        },
        onFailure: (err) => {
          resolve({ failure: true, errorMessage: err.message });
        },
      });
    });
  },

  async completeNewPasswordChallenge(newPassword: string, cognitoUser: CognitoUser, apiGateway: ApiGateway, attributes: { [key: string]: string } = {}): Promise<{status: PasswordChallengeEnum;}> {
    return new Promise((resolve, reject) => {
      cognitoUser.completeNewPasswordChallenge(newPassword, attributes, {
        onSuccess: (session) => {
          LoginService.storeUserAfterSuccessfullyLogin(session, cognitoUser, apiGateway);
          resolve({ status: "Success" });
        },
        onFailure: (err) => {
          console.error("New password challenge failed:", err);
          reject({ status: "Failure" });
        },
      });
    });
  },

  storeUserAfterSuccessfullyLogin(
    session: CognitoUserSession,
    cognitoUser: CognitoUser,
    apiGateway: ApiGateway
  ) {
    console.log('User successfully logged in:', {
      username: cognitoUser.getUsername(),
      email: session.getIdToken().payload.email,
    });
  },

  async forgotPassword(email: string, apiGateway: ApiGateway): Promise<{result?: object; err?: Error}> {
    const poolData = {
      UserPoolId: apiGateway.user_pool_id ?? '',
      ClientId: apiGateway.app_client_id ?? '',
    };
    const userPool = new CognitoUserPool(poolData);
    const userData = {
      Username: email.toLowerCase(),
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          resolve({ result });
        },
        onFailure: (err) => {
          reject({ err });
        },
      });
    });
  },

  async confirmPasswordReset(verificationCode: string, newPassword: string, email: string, apiGateway: ApiGateway): Promise<{ status: RefreshTokenEnum }> {
    const poolData = {
      UserPoolId: apiGateway.user_pool_id ?? '',
      ClientId: apiGateway.app_client_id ?? '',
    };
    const userPool = new CognitoUserPool(poolData);
    const userData = {
      Username: email.toLowerCase(),
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve) => {
      cognitoUser.confirmPassword(verificationCode, newPassword, {
        onSuccess: () => {
          resolve({ status: 'Success' });
        },
        onFailure: (err) => {
          console.error('Password reset failed:', err);
          resolve({ status: 'Failure' });
        },
      });
    });
  },

  async refreshToken(): Promise<{status: RefreshTokenEnum} | null> {
    console.log('Refresh token called.');
    return {status: 'Success'};
  },
};

export default LoginService;